import React, { useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { X, Save, User, DollarSign, Package, CheckCircle } from 'lucide-react';
import styled, { useTheme } from 'styled-components';
import type { DefaultTheme } from 'styled-components';
import { CreateVendaRequest } from '../../types/api';

export const ModalOverlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.theme.colors.overlay};
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: ${props => props.theme.spacing[4]};
`;

export const ModalContent = styled(motion.div)`
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.lg};
  box-shadow: ${props => props.theme.shadows.xl};
  max-width: 760px; /* aumentado para caber mais campos */
  width: 100%;
  max-height: calc(100vh - 16px);
  overflow: visible; /* permitir que dropdown flutue sobre o modal */
`;

export const ModalHeader = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const ModalTitle = styled.h2`
  font-family: ${props => props.theme.typography.fontFamily.heading};
  font-size: ${props => props.theme.typography.fontSize.xl};
  font-weight: ${props => props.theme.typography.fontWeight.semibold};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textSecondary};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  &:hover {
    background: ${props => props.theme.colors.backgroundTertiary};
    color: ${props => props.theme.colors.textPrimary};
    border-color: ${props => props.theme.colors.primary};
  }
`;

export const ModalBody = styled.div`
  padding: ${props => props.theme.spacing[6]};
  max-height: calc(100vh - 240px);
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-gutter: stable;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    max-height: calc(100vh - 200px);
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[6]};
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing[4]};
  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing[2]};
`;

const TallFormGroup = styled(FormGroup)`
  min-height: clamp(164px, 28vh, 240px);
  justify-content: space-evenly;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    min-height: clamp(140px, 24vh, 200px);
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    min-height: auto;
  }
`;

const Label = styled.label<{ $required?: boolean }>`
  font-size: ${ (props: { theme: DefaultTheme }) => props.theme.typography.fontSize.sm };
  font-weight: ${ (props: { theme: DefaultTheme }) => props.theme.typography.fontWeight.medium };
  color: ${ (props: { theme: DefaultTheme }) => props.theme.colors.textSecondary };
  display: flex;
  align-items: center;
  gap: ${ (props: { theme: DefaultTheme }) => props.theme.spacing[2] };
  ${ (props: { theme: DefaultTheme; $required?: boolean }) => props.$required ? `&::after { content: ' *'; color: ${props.theme.colors.error}; }` : '' }
`;

const Input = styled.input<{ $error?: boolean }>`
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[4]};
  background: ${props => props.theme.colors.backgroundTertiary};
  border: 1px solid ${props => props.$error ? props.theme.colors.error : props.theme.colors.border};
  border-radius: ${props => props.theme.borders.radius.md};
  color: ${props => props.theme.colors.textPrimary};
  font-size: ${props => props.theme.typography.fontSize.base};
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  &:focus {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 0 0 3px rgba(255, 68, 68, 0.1);
    outline: none;
  }
  &::placeholder {
    color: ${props => props.theme.colors.textTertiary};
  }
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  font-size: ${props => props.theme.typography.fontSize.sm};
  color: ${props => props.theme.colors.error};
  margin-top: ${props => props.theme.spacing[1]};
`;

export const ModalFooter = styled.div`
  background: ${props => props.theme.colors.backgroundTertiary};
  border-top: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing[6]};
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${props => props.theme.spacing[3]};
`;

export const Button = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing[2]};
  padding: ${props => props.theme.spacing[3]} ${props => props.theme.spacing[6]};
  border-radius: ${props => props.theme.borders.radius.md};
  font-size: ${props => props.theme.typography.fontSize.base};
  font-weight: ${props => props.theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${props => props.theme.animations.duration.fast} ${props => props.theme.animations.easing.easeInOut};
  border: 1px solid transparent;
  ${(props: { theme: DefaultTheme; $variant?: 'primary' | 'secondary' | 'danger' }) => {
    if (props.$variant === 'primary') {
      return `
        background: ${props.theme.colors.primary};
        color: ${props.theme.colors.textPrimary};
        &:hover {
          transform: translateY(-1px);
          box-shadow: ${props.theme.shadows.neon};
          background: ${props.theme.colors.primaryHover};
        }
      `;
    }
    return `
      background: ${props.theme.colors.backgroundSecondary};
      color: ${props.theme.colors.textSecondary};
      border-color: ${props.theme.colors.border};
      &:hover {
        background: ${props.theme.colors.backgroundTertiary};
        color: ${props.theme.colors.textPrimary};
        border-color: ${props.theme.colors.primary};
      }
    `;
  }}
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  }
`;

const statusOptions = [
  { value: 'pago', label: 'Pago' },
  { value: 'pendente', label: 'Pendente' },
  { value: 'cancelada', label: 'Cancelada' },
];

interface SaleFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateVendaRequest) => Promise<void>;
  loading?: boolean;
  initialData?: CreateVendaRequest;
  isEditMode?: boolean;
}

const SaleForm: React.FC<SaleFormProps> = ({
  isOpen,
  onClose,
  onSubmit,
  loading = false,
  initialData,
  isEditMode = false
}) => {
  const [formData, setFormData] = useState<CreateVendaRequest>(
    initialData || {
      cliente: '',
      produto: '',
      valor: '',
      status: 'pendente'
    }
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const formRef = useRef<HTMLFormElement | null>(null);
  const clienteRef = useRef<HTMLInputElement | null>(null);
  const produtoRef = useRef<HTMLInputElement | null>(null);
  const valorRef = useRef<HTMLInputElement | null>(null);
  const statusButtonRef = useRef<HTMLButtonElement | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const theme = useTheme();

  const scrollIntoView = useCallback((element: HTMLElement | null) => {
    if (!element) return;
    requestAnimationFrame(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, []);

  const toggleStatusDropdown = useCallback(() => {
    setDropdownOpen((prev) => {
      const next = !prev;
      if (!prev) {
        scrollIntoView(statusButtonRef.current);
      }
      return next;
    });
  }, [scrollIntoView]);

  const openStatusDropdown = useCallback(() => {
    scrollIntoView(statusButtonRef.current);
    setDropdownOpen(true);
  }, [scrollIntoView]);

  const focusNext = (current: Element | null) => {
    if (!formRef.current) return false;
    const focusable = Array.from(
      formRef.current.querySelectorAll<HTMLElement>(
        'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter(el => el.offsetParent !== null); // visible

    if (focusable.length === 0) return false;

    const idx = focusable.findIndex((el) => el === current);
    const next = idx >= 0 && idx < focusable.length - 1 ? focusable[idx + 1] : null;
    if (next) {
      next.focus();
      return true;
    }
    return false;
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'Enter') return;
    // allow Shift+Enter for new lines if needed
    if (e.shiftKey) return;
    const target = e.target as Element;
    // prevent default form submit on Enter
    e.preventDefault();
    const moved = focusNext(target);
    if (!moved) {
      // no next element, submit form
      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
      formRef.current?.dispatchEvent(submitEvent);
    }
  };

  // field-specific Enter navigation to mimic LeadForm behavior
  const onClienteKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      produtoRef.current?.focus();
    }
  };

  const onProdutoKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      valorRef.current?.focus();
    }
  };

  const onValorKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // open status dropdown and focus it
      statusButtonRef.current?.focus();
      openStatusDropdown();
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.cliente.trim()) newErrors.cliente = 'Cliente é obrigatório';
    if (!formData.produto.trim()) newErrors.produto = 'Produto é obrigatório';
    if (!formData.valor.trim() || isNaN(Number(formData.valor.replace(',', '.')))) newErrors.valor = 'Valor inválido';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      await onSubmit(formData);
      onClose();
    } catch (error) {
      console.error('Erro ao salvar venda:', error);
    }
  };

  const handleInputChange = (field: keyof CreateVendaRequest, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
    >
      <ModalContent
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>
            <DollarSign size={20} />
            {isEditMode ? 'Editar Venda' : 'Nova Venda'}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <X size={16} />
          </CloseButton>
        </ModalHeader>
        <ModalBody>
          <Form ref={formRef} onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
            <FormRow>
              <FormGroup>
                  <Label $required>
                  <User size={16} />
                  Cliente
                </Label>
                <Input
                  type="text"
                  ref={clienteRef}
                  value={formData.cliente}
                  onChange={(e) => handleInputChange('cliente', e.target.value)}
                  onKeyDown={onClienteKeyDown}
                  placeholder="Nome do cliente"
                   $error={!!errors.cliente}
                />
                {errors.cliente && <ErrorMessage>{errors.cliente}</ErrorMessage>}
              </FormGroup>
              <FormGroup>
                  <Label $required>
                  <Package size={16} />
                  Produto
                </Label>
                <Input
                  type="text"
                  ref={produtoRef}
                  value={formData.produto}
                  onChange={(e) => handleInputChange('produto', e.target.value)}
                  onKeyDown={onProdutoKeyDown}
                  placeholder="Nome do produto"
                   $error={!!errors.produto}
                />
                {errors.produto && <ErrorMessage>{errors.produto}</ErrorMessage>}
              </FormGroup>
            </FormRow>
            <FormRow>
              <TallFormGroup>
                  <Label $required>
                  <DollarSign size={16} />
                  Valor
                </Label>
                <Input
                  type="text"
                  ref={valorRef}
                  value={formData.valor}
                  onChange={(e) => handleInputChange('valor', e.target.value.replace(/[^0-9,.]/g, ''))}
                  onKeyDown={onValorKeyDown}
                  placeholder="0,00"
                   $error={!!errors.valor}
                />
                {errors.valor && <ErrorMessage>{errors.valor}</ErrorMessage>}
              </TallFormGroup>
              <TallFormGroup>
                <Label>
                  <CheckCircle size={16} />
                  Status
                </Label>
        {/* Dropdown idêntico ao LeadForm */}
                <div style={{ position: 'relative' }}>
                  <button
                    type="button"
                    ref={statusButtonRef}
                    onClick={toggleStatusDropdown}
                    style={{
          width: '100%',
          padding: theme.spacing[3] + ' ' + theme.spacing[4],
          background: theme.colors.backgroundTertiary,
          border: `1px solid ${theme.colors.border}`,
          borderRadius: theme.borders.radius.md,
          color: theme.colors.textTertiary,
          fontSize: theme.typography.fontSize.base,
          textAlign: 'left',
          cursor: 'pointer',
          outline: 'none'
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (dropdownOpen) {
                          const first = statusOptions[0];
                          if (first) {
                            handleInputChange('status', first.value);
                          }
                          setDropdownOpen(false);
                          // submit after selecting status (last field)
                          const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                          formRef.current?.dispatchEvent(submitEvent);
                        } else {
                          openStatusDropdown();
                        }
                      } else if (e.key === 'ArrowDown') {
                        e.preventDefault();
                        openStatusDropdown();
                      }
                    }}
                  >
                    {statusOptions.find((opt) => opt.value === formData.status)?.label || 'Selecione'}
                  </button>

                  {dropdownOpen && (
                    <ul style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      width: '100%',
                      background: theme.colors.backgroundTertiary,
                      border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borders.radius.md,
                      boxShadow: theme.shadows.md,
                      margin: 0,
                      padding: 0,
                      listStyle: 'none',
                      zIndex: 10,
                      maxHeight: 220,
                      overflowY: 'auto'
                    }}>
                      {statusOptions.map((opt) => (
                        <li key={opt.value}
                          style={{ padding: theme.spacing[3] + ' ' + theme.spacing[4], color: theme.colors.textTertiary, cursor: 'pointer' }}
                          onClick={() => {
                            handleInputChange('status', opt.value);
                            setDropdownOpen(false);
                          }}
                        >
                          {opt.label}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </TallFormGroup>
            </FormRow>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            type="submit"
            $variant="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            <Save size={16} />
              {loading ? 'Salvando...' : (isEditMode ? 'Editar' : 'Criar')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default SaleForm;
